import React, { useState } from 'react';
import './FormField.css';

const EyeIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
        <circle cx="12" cy="12" r="3" />
    </svg>
);

const EyeOffIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94" />
        <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19" />
        <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
);

/**
 * FormField
 *
 * A reusable labeled input field with an optional leading icon and inline
 * error message. Mirrors the style established in the Register page.
 * Password fields automatically show an eye toggle button.
 *
 * @param {string}          label       - Field label text
 * @param {string}          name        - Input name attribute
 * @param {string}          type        - HTML input type (default: 'text')
 * @param {string}          value       - Controlled value
 * @param {function}        onChange    - Change handler
 * @param {string}          placeholder - Placeholder text
 * @param {string|ReactNode} icon       - Leading icon (emoji string or SVG element)
 * @param {string}          error       - Error message; when set the input turns red
 * @param {boolean}         disabled    - Disables the input
 * @param {string}          className   - Extra CSS classes for the root element
 * @param {object}          inputProps  - Any extra props forwarded to the <input>
 */
const FormField = ({
    label,
    name,
    type = 'text',
    value,
    onChange,
    placeholder,
    icon,
    error,
    disabled = false,
    className = '',
    maxLength,
    autoComplete,
    inputProps = {},
}) => {
    const [showPassword, setShowPassword] = useState(false);
    const isPassword = type === 'password';
    const resolvedType = isPassword ? (showPassword ? 'text' : 'password') : type;

    const inputClasses = [
        'ff-input',
        !icon ? 'ff-input--no-icon' : '',
        !isPassword ? 'ff-input--no-eye' : '',
    ].filter(Boolean).join(' ');

    const wrapperClasses = [
        'ff-input-wrapper',
        error ? 'ff-input-wrapper--error' : '',
    ].filter(Boolean).join(' ');

    return (
        <div className={`ff-field ${className}`}>
            {label && <label className="ff-label">{label}</label>}

            <div className={wrapperClasses}>
                {icon && <span className="ff-icon">{icon}</span>}
                <input
                    className={inputClasses}
                    type={resolvedType}
                    name={name}
                    value={value}
                    onChange={onChange}
                    placeholder={placeholder}
                    disabled={disabled}
                    maxLength={maxLength}
                    autoComplete={autoComplete}
                    {...inputProps}
                />
                {isPassword && (
                    <button
                        type="button"
                        className="ff-eye-btn"
                        onClick={() => setShowPassword(v => !v)}
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                        tabIndex={-1}
                    >
                        {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                    </button>
                )}
            </div>

            {error && <span className="ff-error">{error}</span>}
        </div>
    );
};

export default FormField;
